import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { HelpButton } from '@/components/help-system';
import { HelpContent } from '@/components/help-system/help-content';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
  padding?: boolean;
  hideCloseButton?: boolean;
  /**
   * Help content key or custom help content
   * If provided, a help button will be shown in the modal header
   */
  helpKey?: string;
  customHelpContent?: HelpContent;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-2xl',
  padding = true,
  hideCloseButton,
  helpKey,
  customHelpContent
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={maxWidth}
        hideCloseButton={hideCloseButton}
      >
        {/* No custom description id: Radix links Content to Description automatically;
            overriding aria-describedby breaks that linkage and triggers its dev warning. */}
        {title ? (
          <DialogHeader className={padding ? undefined : 'shrink-0'}>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>{title}</DialogTitle>
              {(helpKey || customHelpContent) && (
                <HelpButton
                  helpKey={helpKey || 'default'}
                  customContent={customHelpContent}
                  size="icon"
                  variant="ghost"
                  showLabel={false}
                  className="h-8 w-8"
                  tooltipText="Help for this dialog"
                />
              )}
            </div>
            <DialogDescription className="sr-only">
              {title} dialog content
            </DialogDescription>
          </DialogHeader>
        ) : (
          <DialogDescription className="sr-only">Dialog content</DialogDescription>
        )}
        <div
          className={
            padding
              ? 'p-4'
              : 'flex min-h-0 flex-1 flex-col overflow-hidden'
          }
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
